import React, { useEffect, useState } from 'react';
import Button from '~/components/Button';
import Icon from './Icon';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const PwaInstallDrawer: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const drawerClosed = localStorage.getItem('pwa-drawer-closed');
    if (drawerClosed) return undefined;

    const userAgent = navigator.userAgent || navigator.vendor || '';
    const isAndroidDevice = /android/i.test(userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    setIsAndroid(isAndroidDevice);

    // Pick up the prompt captured globally before React hydrated
    // eslint-disable-next-line no-underscore-dangle
    const cached = (
      window as Window & { __pwaInstallPrompt?: BeforeInstallPromptEvent }
    ).__pwaInstallPrompt;
    if (cached && isAndroidDevice) {
      setDeferredPrompt(cached);
    }

    if (isIOS || isAndroidDevice) {
      setIsVisible(true);
    }

    // Also listen in case the event fires after mount (e.g. re-visit)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      if (isAndroidDevice) {
        setDeferredPrompt(e as BeforeInstallPromptEvent);
        setIsVisible(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt
      );
    };
  }, []);

  const handleClose = () => {
    localStorage.setItem('pwa-drawer-closed', 'true');
    setIsVisible(false);
  };

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    }
    handleClose();
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div
      data-cy="pwa-install-drawer"
      className="fixed left-3 rounded-2xl bg-gray-500 p-6 px-5 pb-4 shadow-hint backdrop-blur-2xl bottom-safe-offset-3 max-md:right-3 md:left-6 md:max-w-md md:bottom-safe-offset-6 z-20 md:hidden"
    >
      <div className="h-base mb-10 space-y-3 *:filter-none *:[text-shadow:none]">
        <h2 className="mb-4">Install Respawwwn on your phone</h2>
        <p className="mb-2">
          Add this app to your home screen for faster access and a better gaming
          experience.
        </p>
      </div>

      <div className="flex gap-2">
        {isAndroid ? (
          <Button
            onClick={handleInstall}
            variant="purple"
            size="small"
            data-cy="pwa-install-button"
          >
            Install
          </Button>
        ) : (
          <p className="paragraph-2-sm mb-4">
            <span className="paragraph-2-sm font-bold">
              Tap{' '}
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="mb-1 inline text-foreground dark:text-foreground-dark"
                focusable="false"
                aria-hidden="true"
              >
                <title>Install</title>
                <g
                  fill="currentColor"
                  strokeLinecap="butt"
                  strokeLinejoin="miter"
                >
                  <polyline
                    fill="none"
                    points="12 16 12 1.5 12 2.5"
                    stroke="currentColor"
                    strokeLinecap="square"
                    strokeMiterlimit="10"
                    strokeWidth="2"
                  />
                  <polyline
                    fill="none"
                    points="8 5.5 12 1.5 16 5.5"
                    stroke="currentColor"
                    strokeLinecap="square"
                    strokeMiterlimit="10"
                    strokeWidth="2"
                  />
                  <path
                    d="m16,10h2c1.105,0,2,.895,2,2v8c0,1.105-.895,2-2,2H6c-1.105,0-2-.895-2-2v-8c0-1.105.895-2,2-2h2"
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="square"
                    strokeMiterlimit="10"
                    strokeWidth="2"
                  />
                </g>
              </svg>{' '}
              then tap "Add to Home Screen"
            </span>
          </p>
        )}

        <Button
          onClick={handleClose}
          variant="red"
          size="small"
          data-cy="pwa-close-button"
        >
          Close
        </Button>
      </div>
    </div>
  );
};

export default PwaInstallDrawer;
